<template>
  <div :class="{ 'monitor-fullscreen': isFullscreen }">
    <el-card shadow="never">
      <div slot="header" class="clearfix">
        <span>交易量实时监控</span>
        <el-button size="mini" :icon="isFullscreen ? 'el-icon-close' : 'el-icon-full-screen'"
          style="float:right;margin-top:-2px" @click="toggleFullscreen">
          {{ isFullscreen ? '退出全屏' : '全屏' }}
        </el-button>
        <el-switch v-model="autoRefresh" active-text="自动刷新" inactive-text=""
          style="margin-left:20px" @change="toggleAutoRefresh"></el-switch>
        <el-switch v-model="carouselEnabled" active-text="自动轮播" inactive-text=""
          style="margin-left:20px" @change="toggleCarousel"></el-switch>
        <el-switch v-model="showCount" active-text="显示笔数" inactive-text=""
          style="margin-left:20px" @change="fetchLineData"></el-switch>
      </div>

      <el-row :gutter="20">
        <!-- 左侧：Top20 应用列表 -->
        <el-col :span="6">
          <div class="app-list-header">日交易量 Top20</div>
          <div class="app-list">
            <div :class="['app-item app-item-all', { active: selectedApplyno === '' }]"
              @click="selectAll()">
              <span class="app-label">全部</span>
              <div class="app-bar-wrap">
                <div class="app-bar" :style="{ width: '100%', background: '#303133' }">
                  <span class="app-bar-text">{{ totalCount }}笔</span>
                </div>
                <span class="app-value">¥{{ totalAmount }}</span>
              </div>
            </div>
            <transition-group name="rank" tag="div">
              <div v-for="(item, index) in totalData" :key="item.applyno || item.applyname"
                :class="['app-item', { active: selectedApp === item.applyname }]"
                @click="selectApp(item)">
                <span class="app-label" :title="item.applyname">{{ item.applyname }}</span>
                <div class="app-bar-wrap">
                  <div class="app-bar" :style="{ width: getBarWidth(item) + '%', background: barColors[index % barColors.length] }">
                    <span class="app-bar-text">{{ item.total }}笔</span>
                  </div>
                  <span class="app-value">¥{{ Number(item.totalAmount || 0).toFixed(2) }}</span>
                </div>
              </div>
            </transition-group>
            <div v-if="totalData.length === 0" style="text-align:center;color:#999;padding:40px 0">
              暂无数据
            </div>
          </div>
        </el-col>

        <!-- 右侧：曲线图 -->
        <el-col :span="18">
          <el-tabs v-model="activeTab" @tab-click="onTabClick" size="small">
            <el-tab-pane label="分钟" name="minute"></el-tab-pane>
            <el-tab-pane label="小时" name="hour"></el-tab-pane>
            <!-- <el-tab-pane label="按日" name="day"></el-tab-pane> -->
          </el-tabs>
          <div v-show="activeTab === 'minute' || activeTab === 'hour'" style="margin-bottom:10px;">
            <el-date-picker v-model="queryDate" type="date" placeholder="选择日期" size="mini"
              :picker-options="pickerOptions" @change="onDateChange">
            </el-date-picker>
          </div>
          <div v-show="activeTab === 'day'" style="margin-bottom:10px">
            <el-form :inline="true" size="mini">
              <el-form-item label="开始日期">
                <el-date-picker v-model="dayStart" type="date" placeholder="开始日期"
                  :picker-options="pickerOptions" @change="onDayDateChange"></el-date-picker>
              </el-form-item>
              <el-form-item label="结束日期">
                <el-date-picker v-model="dayEnd" type="date" placeholder="结束日期"
                  :picker-options="pickerOptions" @change="onDayDateChange"></el-date-picker>
              </el-form-item>
            </el-form>
          </div>
          <div v-if="!selectedApp" style="text-align:center;color:#999;padding:100px 0;font-size:14px">
            ← 请从左侧选择一个应用查看交易量曲线
          </div>
          <div v-show="selectedApp" ref="lineChart" style="width:100%;height:460px;"></div>
        </el-col>
      </el-row>
    </el-card>
  </div>
</template>

<script>
import moment from 'moment'

function loadECharts() {
  return new Promise((resolve, reject) => {
    if (window.echarts) { resolve(window.echarts); return }
    const s = document.createElement('script')
    s.src = 'https://cdn.jsdelivr.net/npm/echarts@5.4.3/dist/echarts.min.js'
    s.onload = () => resolve(window.echarts)
    s.onerror = reject
    document.head.appendChild(s)
  })
}

export default {
  data() {
    return {
      activeTab: 'minute',
      queryDate: new Date(),
      barColors: [
        '#f56c6c', '#e6a23c', '#f2c037', '#67c23a', '#409eff',
        '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de',
        '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc', '#5ab1ef',
        '#d87a80', '#8d98b3', '#e5cf0d', '#97b552', '#95706d'
      ],
      dayStart: moment().subtract(30, 'days').toDate(),
      dayEnd: new Date(),
      autoRefresh: true,
      refreshTimer: null,
      carouselEnabled: true,
      isFullscreen: false,
      carouselTimer: null,
      carouselIndex: -1,
      totalData: [],
      selectedApp: '',
      selectedApplyno: '',
      chartInstance: null,
      echartsLoaded: false,
      showYesterday: true,
      showLastWeek: true,
      showCount: false,
      showAvg7d: true,
      avg7dAmt: null,
      avg7dCnt: null,
      compareYesterdayAmt: [],
      compareYesterdayCnt: [],
      compareLastWeekAmt: [],
      compareLastWeekCnt: [],
      pickerOptions: { disabledDate: (time) => time.getTime() > Date.now() }
    }
  },
  computed: {
    totalCount() {
      return this.totalData.reduce((sum, d) => sum + Number(d.total || 0), 0)
    },
    totalAmount() {
      return this.totalData.reduce((sum, d) => sum + Number(d.totalAmount || 0), 0).toFixed(2)
    }
  },
  mounted() {
    loadECharts().then(() => {
      this.echartsLoaded = true
      window.addEventListener('resize', () => { this.chartInstance && this.chartInstance.resize() })
    })
    this.selectAll()
    this.fetchTotalData()
    this.startAutoRefresh()
    this.startCarousel()
  },
  beforeDestroy() {
    this.stopAutoRefresh()
    this.stopCarousel()
    if (this.chartInstance) this.chartInstance.dispose()
  },
  methods: {
    selectApp(item, fromCarousel) {
      this.selectedApp = item.applyname
      this.selectedApplyno = item.applyno || ''
      if (!fromCarousel) {
        // 手动选择时，更新 carouselIndex 为当前选项的索引
        let currentIndex = this.totalData.findIndex(d => d.applyno === item.applyno || d.applyname === item.applyname)
        this.carouselIndex = currentIndex + 1 // carouselIndex=0 表示"全部"，>=1 表示具体应用
        // 如果轮播开关是开启的，停止旧轮播并立即启动新的轮播
        if (this.carouselEnabled) {
          this.stopCarousel()
          this.startCarousel()
        }
      }
      this.fetchLineData()
    },

    selectAll(fromCarousel) {
      this.selectedApp = '全部'
      this.selectedApplyno = ''
      if (!fromCarousel) {
        // 手动选择"全部"时，更新 carouselIndex 为 0
        this.carouselIndex = 0
        // 如果轮播开关是开启的，停止旧轮播并立即启动新的轮播
        if (this.carouselEnabled) {
          this.stopCarousel()
          this.startCarousel()
        }
      }
      this.fetchLineData()
    },

    getBarWidth(item) {
      if (!this.totalData.length) return 0
      let maxAmt = Math.max(...this.totalData.map(d => Number(d.totalAmount || 0)))
      if (maxAmt === 0) return 0
      let ratio = Number(item.totalAmount || 0) / maxAmt * 100
      // 最小8%，保证短条形也能看到
      return Math.max(8, ratio).toFixed(1)
    },

    onTabClick() {
      if (this.activeTab === 'minute') {
        this.queryDate = new Date()
      }
      this.fetchTotalData()
      this.fetchLineData()
    },
    onDateChange() {
      let isToday = this.queryDate && moment(this.queryDate).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')
      if (isToday) {
        this.autoRefresh = true
        this.startAutoRefresh()
      } else {
        this.autoRefresh = false
        this.stopAutoRefresh()
      }
      this.fetchTotalData()
      this.fetchLineData()
    },
    onHourDateChange() {
      this.fetchTotalData()
      this.fetchHourData()
    },
    onDayDateChange() { this.fetchDayData() },

    fetchTotalData() {
      let _this = this
      let date = this.queryDate ? moment(this.queryDate).format('YYYY-MM-DD') : ''
      this.$ajax({
        url: 'coupon-system/monitor/transVolumeTotal',
        noLoading: true,
        data: { date: date },
        token: window.sessionStorage.getItem('token'),
        success(response) {
          if (response.code == 0) {
            let newData = response.data || []
            if (_this.totalData.length === 0) {
              _this.totalData = newData
            } else {
              // 合并更新：保留已有对象引用，只更新变化的字段
              let oldMap = {}
              _this.totalData.forEach(item => { oldMap[item.applyno || item.applyname] = item })
              let merged = newData.map(item => {
                let key = item.applyno || item.applyname
                let old = oldMap[key]
                if (old) {
                  old.total = item.total
                  old.totalAmount = item.totalAmount
                  return old
                }
                return item
              })
              _this.totalData = merged
            }
            if (!_this.selectedApp && _this.totalData.length > 0) {
              _this.selectAll()
            }
          } else {
            _this.$message.error(response.message)
          }
        }
      })
    },

    fetchLineData() {
      if (!this.selectedApp) return
      if (this.activeTab === 'minute') this.fetchMinuteData()
      else if (this.activeTab === 'hour') this.fetchHourData()
      else if (this.activeTab === 'day') this.fetchDayData()
    },

    // 通用：获取对比日期数据
    _fetchCompareData(url, date, endCount, buildKey, callback) {
      let _this = this
      let pending = 0
      _this.compareYesterdayAmt = []; _this.compareYesterdayCnt = []
      _this.compareLastWeekAmt = []; _this.compareLastWeekCnt = []
      let yesterdayDate = moment(date).subtract(1, 'days').format('YYYY-MM-DD')
      let lastWeekDate = moment(date).subtract(7, 'days').format('YYYY-MM-DD')
      function tryDone() { if (pending <= 0) callback() }
      function parse(response, amtArr, cntArr) {
        if (response.code == 0) {
          let map = {}; (response.data || []).forEach(d => { map[d.timeKey] = d })
          for (let i = 0; i <= endCount; i++) {
            let key = buildKey(i)
            amtArr.push(map[key] ? Number(map[key].amt || 0) : 0)
            cntArr.push(map[key] ? Number(map[key].cnt || 0) : 0)
          }
        }
      }
      if (_this.showYesterday) {
        pending++
        let params = { date: yesterdayDate }
        if (_this.selectedApplyno) {
          params.applyname = _this.selectedApplyno
        }
        _this.$ajax({ url: url, noLoading: true, data: params,
          token: window.sessionStorage.getItem('token'),
          success(r) { parse(r, _this.compareYesterdayAmt, _this.compareYesterdayCnt); pending--; tryDone() }
        })
      }
      if (_this.showLastWeek) {
        pending++
        let params = { date: lastWeekDate }
        if (_this.selectedApplyno) {
          params.applyname = _this.selectedApplyno
        }
        _this.$ajax({ url: url, noLoading: true, data: params,
          token: window.sessionStorage.getItem('token'),
          success(r) { parse(r, _this.compareLastWeekAmt, _this.compareLastWeekCnt); pending--; tryDone() }
        })
      }
      if (!_this.showYesterday && !_this.showLastWeek) tryDone()
      // 获取7天均值
      if (_this.showAvg7d) {
        pending++
        let params = { date: date, days: 7 }
        if (_this.selectedApplyno) {
          params.applyno = _this.selectedApplyno
        }
        _this.$ajax({ url: 'coupon-system/monitor/avgDailySummary', noLoading: true,
          data: params,
          token: window.sessionStorage.getItem('token'),
          success(r) {
            console.log('7天均值接口返回:', r, 'params:', params)
            if (r.code == 0 && r.data) {
              _this.avg7dAmt = Number(r.data.avgAmt || 0)
              _this.avg7dCnt = Number(r.data.avgCnt || 0)
              console.log('赋值后 avg7dAmt:', _this.avg7dAmt, 'avg7dCnt:', _this.avg7dCnt)
            } else {
              _this.avg7dAmt = null; _this.avg7dCnt = null
            }
            pending--; tryDone()
          }
        })
      } else {
        _this.avg7dAmt = null; _this.avg7dCnt = null
      }
    },

    // 构建对比 series
    _buildCompareSeries(legendData, series, smooth) {

      if (this.showYesterday && this.compareYesterdayAmt.length) {
        legendData.push('昨日金额')
        series.push({ name: '昨日金额', type: 'line', smooth: smooth, symbol: 'none', yAxisIndex: 0,
          lineStyle: { type: 'dashed', opacity: 0.8, width: 2 }, itemStyle: { color: '#ff8c00' }, data: this.compareYesterdayAmt })
        if (this.showCount) {
          legendData.push('昨日笔数')
          series.push({ name: '昨日笔数', type: 'line', smooth: smooth, symbol: 'none', yAxisIndex: 1,
            lineStyle: { type: 'dashed', opacity: 0.8, width: 2 }, itemStyle: { color: '#ffa940' }, data: this.compareYesterdayCnt })
        }
      }
      if (this.showLastWeek && this.compareLastWeekAmt.length) {
        legendData.push('上周同天金额')
        series.push({ name: '上周同天金额', type: 'line', smooth: smooth, symbol: 'none', yAxisIndex: 0,
          lineStyle: { type: 'dotted', opacity: 0.8, width: 2 }, itemStyle: { color: '#36cfc9' }, data: this.compareLastWeekAmt })
        if (this.showCount) {
          legendData.push('上周同天笔数')
          series.push({ name: '上周同天笔数', type: 'line', smooth: smooth, symbol: 'none', yAxisIndex: 1,
            lineStyle: { type: 'dotted', opacity: 0.8, width: 2 }, itemStyle: { color: '#87e8de' }, data: this.compareLastWeekCnt })
        }
      }

      if (this.showAvg7d && this.avg7dAmt != null) {
        legendData.push('7天日均金额')
        series.push({ name: '7天日均金额', type: 'line', smooth: false, symbol: 'none', yAxisIndex: 0,
          lineStyle: { type: 'solid', opacity: 0.9, width: 2 }, itemStyle: { color: '#b37feb' },
          markLine: { silent: true, symbol: 'none', lineStyle: { type: 'dashed', color: '#b37feb', width: 2 },
            data: [{ yAxis: this.avg7dAmt, label: { formatter: '7天均值 ¥' + this.avg7dAmt.toFixed(2), position: 'insideEndTop' } }]
          }, data: [] })
        if (this.showCount && this.avg7dCnt != null) {
          legendData.push('7天日均笔数')
          series.push({ name: '7天日均笔数', type: 'line', smooth: false, symbol: 'none', yAxisIndex: 1,
            lineStyle: { type: 'solid', opacity: 0.9, width: 2 }, itemStyle: { color: '#d3adf7' },
            markLine: { silent: true, symbol: 'none', lineStyle: { type: 'dashed', color: '#d3adf7', width: 2 },
              data: [{ yAxis: this.avg7dCnt, label: { formatter: '7天均值 ' + this.avg7dCnt + '笔', position: 'insideEndTop' } }]
            }, data: [] })

        }
      }
    },

    // 分钟级：查整天每分钟数据
    fetchMinuteData() {
      let _this = this
      let date = this.queryDate ? moment(this.queryDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')
      let isToday = date === moment().format('YYYY-MM-DD')
      let params = { date: date }
      if (this.selectedApplyno) {
        params.applyname = this.selectedApplyno
      }
      this.$ajax({
        url: 'coupon-system/monitor/transVolume',
        noLoading: true,
        data: params,
        token: window.sessionStorage.getItem('token'),
        success(response) {
          if (response.code == 0) {
            let data = response.data || []
            let endMinute = isToday ? moment().hour() * 60 + moment().minute() : 24 * 60 - 1
            let dataMap = {}
            data.forEach(d => { dataMap[d.timeKey] = d })
            let fullTime = [], fullAmt = [], fullCnt = []
            for (let m = 0; m <= endMinute; m++) {
              let hh = String(Math.floor(m / 60)).padStart(2, '0')
              let mm = String(m % 60).padStart(2, '0')
              let key = hh + ':' + mm
              fullTime.push(key)
              fullAmt.push(dataMap[key] ? Number(dataMap[key].amt || 0) : 0)
              fullCnt.push(dataMap[key] ? Number(dataMap[key].cnt || 0) : 0)
            }
            _this._fetchCompareData('coupon-system/monitor/transVolume', date, endMinute,
              function(i) { return String(Math.floor(i / 60)).padStart(2, '0') + ':' + String(i % 60).padStart(2, '0') },
              function() { _this.renderMinuteChart(fullTime, fullAmt, fullCnt, _this.selectedApp + ' - 分钟级（' + date + '）') }
            )
          } else { _this.$message.error(response.message) }
        }
      })
    },

    // 小时级：查 sam_order（coupon-merch）
    fetchHourData() {
      let _this = this
      let date = this.queryDate ? moment(this.queryDate).format('YYYY-MM-DD') : ''
      let isToday = date === moment().format('YYYY-MM-DD')
      let params = { date: date }
      if (this.selectedApplyno) {
        params.applyname = this.selectedApplyno
      }
      this.$ajax({
        url: 'coupon-system/monitor/transVolumeHour',
        noLoading: true,
        data: params,
        token: window.sessionStorage.getItem('token'),
        success(response) {
          if (response.code == 0) {
            let data = response.data || []
            let endHour = isToday ? moment().hour() : 23
            let dataMap = {}
            data.forEach(d => { dataMap[d.timeKey] = d })
            let fullTime = [], fullAmt = [], fullCnt = []
            for (let h = 0; h <= endHour; h++) {
              let key = String(h).padStart(2, '0') + ':00'
              fullTime.push(key)
              fullAmt.push(dataMap[key] ? Number(dataMap[key].amt || 0) : 0)
              fullCnt.push(dataMap[key] ? Number(dataMap[key].cnt || 0) : 0)
            }
            _this._fetchCompareData('coupon-system/monitor/transVolumeHour', date, endHour,
              function(i) { return String(i).padStart(2, '0') + ':00' },
              function() { _this.renderChart(fullTime, fullAmt, fullCnt, _this.selectedApp + ' - 小时级（' + (date || '今天') + '）', 0, 0) }
            )
          } else { _this.$message.error(response.message) }
        }
      })
    },

    // 按日：查 sam_summary 表（coupon-system）
    fetchDayData() {
      let _this = this
      let start = this.dayStart ? moment(this.dayStart).format('YYYY-MM-DD') : moment().subtract(30, 'days').format('YYYY-MM-DD')
      let end = this.dayEnd ? moment(this.dayEnd).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')
      let params = { start: start, end: end }
      if (this.selectedApplyno) {
        params.applyname = this.selectedApplyno
      }
      this.$ajax({
        url: 'coupon-system/monitor/queryDailySummary',
        noLoading: true,
        method: 'POST',
        data: JSON.stringify(params),
        contentType: 'application/json',
        token: window.sessionStorage.getItem('token'),
        success(response) {
          if (response.code == 0) {
            let list = response.data || []
            // 按日期正序
            list.sort((a, b) => a.date > b.date ? 1 : -1)
            let timeAxis = list.map(d => d.date)
            let values = list.map(d => Number(d.totalamount || 0))
            let counts = list.map(d => Number(d.fscount || 0))
            _this.renderChart(timeAxis, values, counts, _this.selectedApp + ' - 按日（' + start + ' ~ ' + end + '）', 45, 1)
          } else { _this.$message.error(response.message) }
        }
      })
    },

    renderMinuteChart(timeAxis, values, counts, title) {
      if (!this.echartsLoaded) {
        setTimeout(() => this.renderMinuteChart(timeAxis, values, counts, title), 200)
        return
      }
      this.$nextTick(() => {
        if (!this.chartInstance) { this.chartInstance = window.echarts.init(this.$refs.lineChart) }
        this.chartInstance.resize()
        let legendData = ['消费金额']
        let series = [
          { name: '消费金额', type: 'line', smooth: false, symbol: 'none', yAxisIndex: 0, areaStyle: { opacity: 0.2 }, itemStyle: { color: '#5470c6' }, data: values }
        ]
        if (this.showCount) {
          legendData.push('消费笔数')
          series.push({ name: '消费笔数', type: 'line', smooth: false, symbol: 'none', yAxisIndex: 1, areaStyle: { opacity: 0.1 }, itemStyle: { color: '#91cc75' }, data: counts })
        }
        this._buildCompareSeries(legendData, series, false, values.length)
        console.log('renderMinuteChart - legendData:', legendData, 'series数量:', series.length, 'avg7dAmt:', this.avg7dAmt)
        console.log('series详情:', JSON.stringify(series, null, 2))
        this.chartInstance.setOption({
          title: { text: title, left: 'center', textStyle: { fontSize: 14 } },
          tooltip: { trigger: 'axis', axisPointer: { type: 'cross' },
            formatter: function(params) {
              var tip = params[0].axisValue
              params.forEach(function(p) {
                tip += '<br/>' + p.marker + p.seriesName + ': ' + (p.seriesName.indexOf('金额') > -1 ? '¥' + Number(p.value).toFixed(2) : p.value + ' 笔')
              })
              return tip
            }
          },
          legend: { data: legendData, top: '6%' },
          grid: { left: '3%', right: '4%', bottom: '18%', top: '18%', containLabel: true },
          dataZoom: [ { type: 'slider', start: 0, end: 100, bottom: '2%', height: 20 }, { type: 'inside', start: 0, end: 100 } ],
          xAxis: { type: 'category', boundaryGap: false, data: timeAxis, axisLabel: { rotate: 0, interval: 59 } },
          yAxis: [
            { type: 'value', name: '消费金额(元)', position: 'left', axisLabel: { formatter: '¥{value}' } },
            { type: 'value', name: '消费笔数', position: 'right', axisLabel: { formatter: '{value}' }, splitLine: { show: false } }
          ],
          series: series
        }, true)
      })
    },

    renderChart(timeAxis, values, counts, title, rotate, interval) {
      if (!this.echartsLoaded) {
        setTimeout(() => this.renderChart(timeAxis, values, counts, title, rotate, interval), 200)
        return
      }
      this.$nextTick(() => {
        if (!this.chartInstance) { this.chartInstance = window.echarts.init(this.$refs.lineChart) }
        this.chartInstance.resize()
        let legendData = ['消费金额']
        let series = [
          { name: '消费金额', type: 'line', smooth: false, symbol: 'circle', symbolSize: 4, yAxisIndex: 0, areaStyle: { opacity: 0.15 }, itemStyle: { color: '#5470c6' }, data: values }
        ]
        if (this.showCount) {
          legendData.push('消费笔数')
          series.push({ name: '消费笔数', type: 'line', smooth: false, symbol: 'diamond', symbolSize: 4, yAxisIndex: 1, areaStyle: { opacity: 0.1 }, itemStyle: { color: '#91cc75' }, data: counts })
        }
        this._buildCompareSeries(legendData, series, false, values.length)
        console.log('renderChart - legendData:', legendData, 'series数量:', series.length, 'avg7dAmt:', this.avg7dAmt)
        this.chartInstance.setOption({
          title: { text: title, left: 'center', textStyle: { fontSize: 14 } },
          tooltip: { trigger: 'axis', axisPointer: { type: 'cross' },
            formatter: function(params) {
              var tip = params[0].axisValue
              params.forEach(function(p) {
                tip += '<br/>' + p.marker + p.seriesName + ': ' + (p.seriesName.indexOf('金额') > -1 ? '¥' + Number(p.value).toFixed(2) : p.value + ' 笔')
              })
              return tip
            }
          },
          legend: { data: legendData, top: '6%' },
          grid: { left: '3%', right: '4%', bottom: '10%', top: '18%', containLabel: true },
          xAxis: { type: 'category', boundaryGap: false, data: timeAxis, axisLabel: { rotate: rotate, interval: interval } },
          yAxis: [
            { type: 'value', name: '消费金额(元)', position: 'left', axisLabel: { formatter: '¥{value}' } },
            { type: 'value', name: '消费笔数', position: 'right', axisLabel: { formatter: '{value}' }, splitLine: { show: false } }
          ],
          series: series
        }, true)
      })
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
      if (this.isFullscreen && this.carouselEnabled) {
        this.startCarousel()
      } else {
        this.stopCarousel()
      }
      this.$nextTick(() => {
        if (this.chartInstance) this.chartInstance.resize()
      })
    },
    startCarousel() {
      this.stopCarousel()
      if (!this.carouselEnabled) return
      this.carouselTimer = setInterval(() => {
        if (this.totalData.length === 0) return
        this.carouselIndex++
        // 循环：全部 -> 第1个 -> 第2个 -> ... -> 最后一个 -> 全部
        if (this.carouselIndex > this.totalData.length) this.carouselIndex = 0
        if (this.carouselIndex === 0) {
          this.selectAll(true)
        } else {
          this.selectApp(this.totalData[this.carouselIndex - 1], true)
        }
      }, 5000)
    },
    stopCarousel() {
      if (this.carouselTimer) { clearInterval(this.carouselTimer); this.carouselTimer = null }
    },
    toggleCarousel() {
      if (this.carouselEnabled) {
        this.startCarousel()
      } else {
        this.stopCarousel()
      }
    },
    toggleAutoRefresh(val) { val ? this.startAutoRefresh() : this.stopAutoRefresh() },
    startAutoRefresh() {
      this.stopAutoRefresh()
      this.refreshTimer = setInterval(() => {
        if (this.autoRefresh) {
          this.queryDate = new Date()
          this.fetchTotalData(); this.fetchLineData()
        }
      }, 60000)
    },
    stopAutoRefresh() {
      if (this.refreshTimer) { clearInterval(this.refreshTimer); this.refreshTimer = null }
    }
  }
}
</script>

<style scoped>
.app-list-header {
  font-size: 14px; font-weight: bold; padding: 10px 12px;
  background: #f5f7fa; border-bottom: 1px solid #ebeef5;
}
.app-list {
  max-height: 650px; overflow-y: auto;
  border: 1px solid #ebeef5; border-top: none;
}
.app-item {
  display: flex; align-items: center; padding: 4px 8px;
  cursor: pointer; border-bottom: 1px solid #f5f5f5; transition: background 0.2s;
}
.app-item:hover { background: #f5f7fa; }
.app-item.active { background: #ecf5ff; }
.app-item-all { border-bottom: 2px solid #dcdfe6; }
.app-label {
  width: 58px; flex-shrink: 0; font-size: 11px; color: #606266;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; text-align: right;
  padding-right: 4px;
}
.app-item:nth-child(1) .app-rank,
.app-item:nth-child(2) .app-rank,
.app-item:nth-child(3) .app-rank { background: #f56c6c; color: #fff; }
.app-info { overflow: hidden; flex: 1; }
.app-top-row { display: flex; justify-content: space-between; align-items: center; }
.app-name { font-size: 12px; color: #303133; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; }
.app-bar-wrap {
  flex: 1; display: flex; align-items: center; height: 20px; background: #f5f5f5; border-radius: 2px; position: relative;
}
.app-bar {
  height: 100%; border-radius: 2px; display: flex; align-items: center; min-width: 0;
  transition: width 0.4s ease; position: relative; overflow: visible;
}
.app-bar-text {
  font-size: 10px; color: #fff; padding-left: 4px; white-space: nowrap;
  position: absolute; left: 0; text-shadow: 0 0 3px rgba(0,0,0,0.5);
}
.app-value {
  font-size: 11px; color: #606266; margin-left: 4px; white-space: nowrap; flex-shrink: 0;
}
.app-count { font-size: 11px; color: #909399; white-space: nowrap; margin-left: 6px; }
.rank-move { transition: transform 0.8s ease; }
.monitor-fullscreen {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  z-index: 1000;
  background: #fff; overflow: auto; padding: 10px;
}
.monitor-fullscreen /deep/ .el-date-picker {
  z-index: 1100 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/trans_monitor.vue